// Pseudo-3D renderer. Platforms are flattened ellipses; the trunk is drawn over
// the BACK half of every ring and under the FRONT half → depth without WebGL.
// Perf rules (ТЗ 5.3): flat fills, cached trunk gradient, no shadowBlur, and zero
// per-frame allocation — wedge vertices go straight into the path (no arrays).
import {
  W, H, CX, CY, SECTORS_PER_RING, ARC_TESS, PLATFORM_RX, FLATTEN, PLATFORM_THICK,
  TRUNK_R, LEVEL_GAP, APPLE_R, MAX_DPR, GREEN_FOLD_T, SMASH_T,
} from './config';
import { Theme, Ring, Particle, Faller, Score, Phase } from './types';
import { Tower, sectorRange } from './tower';

const TWO_PI = Math.PI * 2;

export interface Scene {
  tower: Tower;
  faller: Faller;
  camWorldY: number;
  towerAngle: number;
  t: number;          // seconds (for moving sectors)
  now: number;        // ms
  squashY: number;    // 1 = none, <1 = squashed faller
  particles: Particle[];
  sprite: HTMLImageElement | null;
  score: Score;
  phase: Phase;
}

export function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

export function readTheme(): Theme {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  return {
    pageBg: v('--page-bg'),
    sky: v('--sky'), water: v('--water'), cloud: v('--cloud'),
    trunk: v('--trunk'), trunkHi: v('--trunk-hi'), trunkLo: v('--trunk-lo'), trunkRing: v('--trunk-ring'),
    safe: v('--safe'), safeEdge: v('--safe-edge'),
    red: v('--red'), redEdge: v('--red-edge'),
    green: v('--green'), greenEdge: v('--green-edge'),
    ball: v('--ball'), ballHi: v('--ball-hi'), ballEdge: v('--ball-edge'), reactive: v('--reactive'),
    barFrame: v('--bar-frame'), barTrough: v('--bar-trough'), barFill: v('--bar-fill'), barMark: v('--bar-mark'),
    badgeBg: v('--badge-bg'), badgeTx: v('--badge-tx'), closeBg: v('--close-bg'),
    text: v('--text'), textLight: v('--text-light'), overlayBg: v('--overlay-bg'),
    btnBg: v('--btn-bg'), btnTx: v('--btn-tx'),
  };
}

const screenY = (worldY: number, camWorldY: number): number => CY + (worldY - camWorldY);

// ── cached trunk cylinder gradient (built once) ──
let trunkGrad: CanvasGradient | null = null;
function getTrunkGrad(ctx: CanvasRenderingContext2D, theme: Theme): CanvasGradient {
  if (!trunkGrad) {
    const g = ctx.createLinearGradient(CX - TRUNK_R, 0, CX + TRUNK_R, 0);
    g.addColorStop(0, theme.trunkHi);
    g.addColorStop(0.45, theme.trunk);
    g.addColorStop(1, theme.trunkLo);
    trunkGrad = g;
  }
  return trunkGrad;
}

function sectorFill(theme: Theme, type: string, edge: boolean): string {
  if (type === 'safe') return edge ? theme.safeEdge : theme.safe;
  if (type === 'red') return edge ? theme.redEdge : theme.red;
  return edge ? theme.greenEdge : theme.green;   // green
}

// Trace an annular wedge directly into the current path (no allocation).
function wedgePath(
  ctx: CanvasRenderingContext2D, cy: number, sa0: number, sa1: number, rIn: number, rOut: number,
): void {
  ctx.beginPath();
  for (let i = 0; i <= ARC_TESS; i++) {
    const th = sa0 + (sa1 - sa0) * (i / ARC_TESS);
    const x = CX + rOut * Math.cos(th);
    const y = cy + rOut * FLATTEN * Math.sin(th);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  for (let i = ARC_TESS; i >= 0; i--) {
    const th = sa0 + (sa1 - sa0) * (i / ARC_TESS);
    ctx.lineTo(CX + rIn * Math.cos(th), cy + rIn * FLATTEN * Math.sin(th));
  }
  ctx.closePath();
}

// Trunk ring (обруч): only the BACK (upper) arc, so it reads as a band wrapping
// behind the disc and never dips onto the bare trunk below as a "cup".
function ellipseStroke(ctx: CanvasRenderingContext2D, cy: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i <= 12; i++) {
    const th = Math.PI + (i / 12) * Math.PI;   // π..2π → sin<0 → upper arc
    const x = CX + r * Math.cos(th);
    const y = cy + r * FLATTEN * Math.sin(th);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

// Removal anim factors for a folding/smashing ring.
function removeFactors(ring: Ring, now: number): { rIn: number; rOut: number; alpha: number } {
  if (!ring.removing) return { rIn: TRUNK_R, rOut: PLATFORM_RX, alpha: 1 };
  const start = ring.removeStart || now;
  if (ring.removing === 'green') {
    const p = Math.min((now - start) / GREEN_FOLD_T, 1);
    return { rIn: TRUNK_R, rOut: PLATFORM_RX + (TRUNK_R + 2 - PLATFORM_RX) * p, alpha: 1 - p };
  }
  const p = Math.min((now - start) / SMASH_T, 1);
  return { rIn: TRUNK_R + 90 * p, rOut: PLATFORM_RX + 90 * p, alpha: 1 - p };
}

function drawBackground(ctx: CanvasRenderingContext2D, theme: Theme): void {
  const waterY = H * 0.52;
  ctx.fillStyle = theme.sky;
  ctx.fillRect(0, 0, W, waterY);
  ctx.fillStyle = theme.water;
  ctx.fillRect(0, waterY, W, H - waterY);
  // two static clouds
  ctx.fillStyle = theme.cloud;
  ctx.globalAlpha = 0.85;
  cloud(ctx, 150, 180, 70);
  cloud(ctx, 560, 300, 54);
  ctx.globalAlpha = 1;
}
function cloud(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TWO_PI);
  ctx.arc(x + r, y + 6, r * 0.8, 0, TWO_PI);
  ctx.arc(x - r * 0.9, y + 8, r * 0.7, 0, TWO_PI);
  ctx.fill();
}

export function renderScene(ctx: CanvasRenderingContext2D, theme: Theme, s: Scene): void {
  drawBackground(ctx, theme);

  const cam = s.camWorldY;
  const margin = PLATFORM_RX * FLATTEN + PLATFORM_THICK + 40;
  const loLevel = Math.floor((cam - CY - margin) / LEVEL_GAP);
  const hiLevel = Math.ceil((cam + (H - CY) + margin) / LEVEL_GAP);

  // 1) BACK halves (behind trunk)
  for (let lvl = loLevel; lvl <= hiLevel; lvl++) {
    const ring = s.tower.rings.get(lvl);
    if (ring) drawRingHalf(ctx, theme, ring, s, screenY(ring.worldY, cam), false);
  }

  // 2) trunk + обручи
  ctx.fillStyle = getTrunkGrad(ctx, theme);
  ctx.fillRect(CX - TRUNK_R, 0, TRUNK_R * 2, H);
  ctx.strokeStyle = theme.trunkRing;
  ctx.lineWidth = 5;
  for (let lvl = loLevel; lvl <= hiLevel; lvl++) {
    const ring = s.tower.rings.get(lvl);
    if (ring) ellipseStroke(ctx, screenY(ring.worldY, cam), TRUNK_R);
  }

  // 3) FRONT halves (over trunk) — these are what the faller hits
  for (let lvl = loLevel; lvl <= hiLevel; lvl++) {
    const ring = s.tower.rings.get(lvl);
    if (ring) drawRingHalf(ctx, theme, ring, s, screenY(ring.worldY, cam), true);
  }

  drawFaller(ctx, theme, s);
  drawParticles(ctx, s.particles);
}

// Portion of screen-angle arc [sa0,sa1] belonging to the requested half. front =
// sin>0 (near/bottom). A 45° sector that straddles a side (θ=0 or π) is split so
// each part draws in its correct layer — otherwise the misplaced part leaves a
// see-through sliver around the trunk.
const SEAM_OVERLAP = 0.13;   // each half extends a little past the side boundary so
                             // the front wall covers the front/back step (no edge sliver)
function halfSubRange(sa0: number, sa1: number, front: boolean): [number, number] | null {
  const fStart = Math.sin(sa0) > 0;
  const fEnd = Math.sin(sa1) > 0;
  if (fStart === fEnd) return fStart === front ? [sa0, sa1] : null;
  const c = Math.ceil(sa0 / Math.PI) * Math.PI;   // the single boundary crossing
  if (fStart === front) return [sa0, Math.min(sa1, c + SEAM_OVERLAP)];
  return [Math.max(sa0, c - SEAM_OVERLAP), sa1];
}

// Small angular overlap so adjacent flat-filled wedges don't leave AA hairlines
// (the "tiny gaps"). Collision uses the unpadded ranges, so logic is unaffected.
const SEAM = 0.012;

// Outer side wall (боковина): a band of height THICK along the front outer arc.
function outerBand(ctx: CanvasRenderingContext2D, cy: number, sa0: number, sa1: number, rOut: number): void {
  ctx.beginPath();
  for (let i = 0; i <= ARC_TESS; i++) {
    const th = sa0 + (sa1 - sa0) * (i / ARC_TESS);
    const x = CX + rOut * Math.cos(th);
    const y = cy + rOut * FLATTEN * Math.sin(th);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  for (let i = ARC_TESS; i >= 0; i--) {
    const th = sa0 + (sa1 - sa0) * (i / ARC_TESS);
    ctx.lineTo(CX + rOut * Math.cos(th), cy + PLATFORM_THICK + rOut * FLATTEN * Math.sin(th));
  }
  ctx.closePath();
}

// Radial cut wall (торец среза) at a sector edge exposed to a gap.
function radialWall(ctx: CanvasRenderingContext2D, cy: number, ang: number, rIn: number, rOut: number): void {
  const c = Math.cos(ang), s = Math.sin(ang) * FLATTEN;
  const xi = CX + rIn * c, yi = cy + rIn * s;
  const xo = CX + rOut * c, yo = cy + rOut * s;
  ctx.beginPath();
  ctx.moveTo(xi, yi);
  ctx.lineTo(xo, yo);
  ctx.lineTo(xo, yo + PLATFORM_THICK);
  ctx.lineTo(xi, yi + PLATFORM_THICK);
  ctx.closePath();
}

const EDGE_EPS = 0.02;
// Signed angular gap x−y normalised to (−π,π].
function angDiff(x: number, y: number): number {
  let d = x - y;
  while (d > Math.PI) d -= TWO_PI;
  while (d < -Math.PI) d += TWO_PI;
  return d;
}

function drawRingHalf(
  ctx: CanvasRenderingContext2D, theme: Theme, ring: Ring, s: Scene, cy: number, front: boolean,
): void {
  const { rIn, rOut, alpha } = removeFactors(ring, s.now);
  if (alpha <= 0) return;
  ctx.globalAlpha = alpha;
  const N = SECTORS_PER_RING;
  for (let i = 0; i < N; i++) {
    const sec = ring.sectors[i];
    if (sec.type === 'gap') continue;
    const [a0, a1] = sectorRange(sec, s.t);
    const sa0 = a0 + s.towerAngle, sa1 = a1 + s.towerAngle;
    const sub = halfSubRange(sa0 - SEAM, sa1 + SEAM, front);
    if (!sub) continue;
    const [b0, b1] = sub;
    ctx.fillStyle = sectorFill(theme, sec.type, true);   // thickness colour
    // near outer rim (боковина) — only the front arc faces the viewer
    if (front) { outerBand(ctx, cy, b0, b1, rOut); ctx.fill(); }
    // radial cut walls wherever an edge faces an OPENING — a gap OR a neighbour that
    // has slid away (moving sectors). Drawn in whichever half the edge faces.
    const prev = ring.sectors[(i - 1 + N) % N];
    const next = ring.sectors[(i + 1) % N];
    const openA0 = prev.type === 'gap' || angDiff(a0, sectorRange(prev, s.t)[1]) > EDGE_EPS;
    const openA1 = next.type === 'gap' || angDiff(sectorRange(next, s.t)[0], a1) > EDGE_EPS;
    if (openA0 && (Math.sin(sa0) > 0) === front) { radialWall(ctx, cy, sa0, rIn, rOut); ctx.fill(); }
    if (openA1 && (Math.sin(sa1) > 0) === front) { radialWall(ctx, cy, sa1, rIn, rOut); ctx.fill(); }
    wedgePath(ctx, cy, b0, b1, rIn, rOut);     // top face
    ctx.fillStyle = sectorFill(theme, sec.type, false);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawFaller(ctx: CanvasRenderingContext2D, theme: Theme, s: Scene): void {
  if (s.faller.state === 'dead') return;       // shards drawn as particles
  const x = CX;
  const y = screenY(s.faller.worldY, s.camWorldY);
  const r = APPLE_R;
  const sy = s.squashY;

  if (s.faller.reactive) {                      // reactive halo (no shadowBlur)
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = theme.reactive;
    ctx.beginPath();
    ctx.arc(x, y, r + 7, 0, TWO_PI);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, sy);
  if (s.sprite && s.sprite.complete && s.sprite.naturalWidth) {
    ctx.drawImage(s.sprite, -r, -r, r * 2, r * 2);
  } else {
    const base = s.faller.reactive ? theme.reactive : theme.ball;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, TWO_PI);
    ctx.fillStyle = base;
    ctx.fill();
    const g = ctx.createRadialGradient(-r * 0.35, -r * 0.4, r * 0.1, 0, 0, r);
    g.addColorStop(0, theme.ballHi);
    g.addColorStop(0.55, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = theme.ballEdge;
    ctx.stroke();
  }
  ctx.restore();
}

function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[]): void {
  for (const p of particles) {
    ctx.globalAlpha = Math.max(0, p.life / p.max);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, TWO_PI);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// Faller screen-Y (shared with main for spawn points).
export const fallerScreenY = (worldY: number, camWorldY: number): number => screenY(worldY, camWorldY);
